import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { TextField, Toggle, BoolPill } from './fields';
import EditHistoryLink from './EditHistoryLink';
import CarrierContactsPanel from './CarrierContactsPanel';
import RmisLookupModal from './RmisLookupModal';
import type { CustomDrawerProps } from './TablemaintPage';
import type { RmisCarrier } from '../../services/rmis.service';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';

/**
 * Bespoke edit/create drawer for Carrier Profiles (highway.carrierprofile).
 *
 * Mirrors V1 editcarrierprofile:
 *  - RMIS Y/N: when N, the DOT #/MC #/SCAC fields are read-only (they come from
 *    RMIS); when Y they're editable and either MC # or DOT # is required to save.
 *  - "RMIS Lookup" next to DOT #/MC # queries RMIS and (via the results modal)
 *    can copy the returned carrier fields — including Confirmed + Last Confirmed,
 *    which are otherwise read-only — into the record.
 *  - Soft-delete is the inverted `active` flag: the engine maps the Active toggle
 *    to active via soft_delete_active='Y' / soft_delete_deleted='N', so we send
 *    active='N' (not-deleted) when active and 'Y' (deleted) when inactive.
 *  - Contacts (highway.carriercontact) can only be added after the profile is
 *    saved, so the contacts panel is edit-mode only.
 *  - Duplicate DOT # is rejected by the engine (keys=['dotnum'], skip-blank).
 */
const TABLE = 'carrierprofile';

// Editable scalar columns (comments is the textarea; rmis and tracking are the
// Y/N toggles).
const SCALAR_KEYS = [
  'truckername', 'rmis', 'tracking', 'truckerscac', 'mcnum', 'dotnum',
  'truckeraddress', 'truckercity', 'truckerstate', 'truckerzip', 'comments',
];

const s = (v: any): string => (v == null ? '' : String(v));

/** Format an ISO/space datetime ('YYYY-MM-DD[ T]HH:MM[:SS]') as 'MM/DD/YYYY HH:MM' (blank when unset). */
const fmtDateTime = (v: any): string => {
  const sv = s(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(sv);
  if (!m || m[1] === '0000') return '';
  return `${m[2]}/${m[3]}/${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ''}`;
};

const inputCls = (invalid?: boolean, disabled?: boolean): string =>
  `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
    disabled ? 'bg-gray-50 dark:bg-gray-800/60 text-muted cursor-not-allowed' : ''
  }${invalid ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'}`;

const LockedField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {children}
    </div>
  </div>
);

/** The base Table-Maintenance drawer props plus two optional hooks used when the
 *  drawer is opened INLINE as a create form (e.g. "Add As New Carrier" from the
 *  Pro drawer's Carrier typeahead): seed the name from what the user typed and
 *  hand the freshly-created carrier back so the caller can auto-link it. */
interface Props extends CustomDrawerProps {
  /** Seeds Carrier Name for a fresh create (ignored in edit mode). */
  initialName?: string;
  /** Fires after a successful create with the new carrier's key + display fields. */
  onCreated?: (c: { recno: number; truckername: string; truckerscac: string }) => void;
}

const CarrierProfileEditDrawer: React.FC<Props> = ({ row, onClose, onSaved, initialName, onCreated }) => {
  const isNew = !row;
  const recno = row ? Number(row.recno) : 0;

  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [form, setForm] = useState<TablemaintRow>({});
  // Confirmed + Last Confirmed are read-only in the UI; only RMIS "Update Carrier"
  // changes them, so they're held separately from the editable scalar form.
  const [confirmed, setConfirmed] = useState('N');
  const [lastConfirm, setLastConfirm] = useState('');
  const [formActive, setFormActive] = useState(true);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [rmisModal, setRmisModal] = useState<{ queryType: 'DOT' | 'MC_MX'; queryId: string } | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  const wasActive = s(record.active) === 'Y';

  useEffect(() => {
    if (!isNew) tablemaintService.get(TABLE, recno).then(setRecord).catch(() => {});
  }, [isNew, recno]);

  // Seed the form from the record (rmis defaults to 'N' for a new carrier).
  // For an inline create, seed Carrier Name from initialName (what the user
  // typed in the typeahead) when the record carries no name yet.
  useEffect(() => {
    const f: TablemaintRow = {};
    SCALAR_KEYS.forEach((k) => { f[k] = s(record[k]); });
    if (!s(f.rmis)) f.rmis = 'N';
    if (!s(f.tracking)) f.tracking = 'N';
    if (isNew && initialName && !s(f.truckername)) f.truckername = initialName;
    setForm(f);
    setConfirmed(s(record.confirmed) || 'N');
    setLastConfirm(s(record.lastconfirmdatetime));
    setFormActive(isNew ? true : s(record.active) === 'Y');
  }, [record, isNew, initialName]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const rmisOn = s(form.rmis) === 'Y';
  const trackingOn = s(form.tracking) === 'Y';

  const errors = useMemo(() => {
    const errs: string[] = [];
    if (!s(form.truckername).trim()) errs.push('Carrier Name is required.');
    if (rmisOn && !s(form.mcnum).trim() && !s(form.dotnum).trim()) errs.push('MC # or DOT # is required when RMIS is enabled.');
    return errs;
  }, [form, rmisOn]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];
  const nameInvalid = showErrors && !s(form.truckername).trim();
  const idInvalid = showErrors && rmisOn && !s(form.mcnum).trim() && !s(form.dotnum).trim();

  // Copy RMIS-returned carrier fields into the form (V1 "Update Carrier").
  const applyRmis = useCallback((c: RmisCarrier) => {
    setForm((p) => ({
      ...p,
      truckername: c.truckername ?? p.truckername,
      truckerscac: c.truckerscac ?? p.truckerscac,
      mcnum: c.mcnum ?? p.mcnum,
      dotnum: c.dotnum ?? p.dotnum,
      truckeraddress: c.truckeraddress ?? p.truckeraddress,
      truckercity: c.truckercity ?? p.truckercity,
      truckerstate: c.truckerstate ?? p.truckerstate,
      truckerzip: c.truckerzip ?? p.truckerzip,
    }));
    if (c.confirmed != null) setConfirmed(c.confirmed);
    if (c.lastconfirmdatetime != null && c.lastconfirmdatetime !== '') setLastConfirm(c.lastconfirmdatetime);
  }, []);

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isNew) {
        const payload: TablemaintRow = {};
        SCALAR_KEYS.forEach((k) => { payload[k] = s(form[k]); });
        payload.confirmed = s(confirmed) || 'N';
        payload.lastconfirmdatetime = s(lastConfirm);
        const res = await tablemaintService.create(TABLE, payload);
        // Hand the new carrier back so an inline caller (e.g. the Pro drawer's
        // Carrier typeahead) can auto-link it onto the order.
        onCreated?.({
          recno: Number(res?.recno) || 0,
          truckername: s(form.truckername),
          truckerscac: s(form.truckerscac),
        });
      } else {
        const patch: TablemaintRow = {};
        SCALAR_KEYS.forEach((k) => { if (s(form[k]) !== s(record[k])) patch[k] = s(form[k]); });
        if (s(confirmed) !== s(record.confirmed)) patch.confirmed = s(confirmed);
        if (s(lastConfirm) !== s(record.lastconfirmdatetime)) patch.lastconfirmdatetime = s(lastConfirm);
        // Inverted soft-delete: send active='N' (not-deleted) when active, 'Y'
        // (deleted) when inactive — the engine maps it to the stored flag.
        if (formActive !== wasActive) patch.active = formActive ? 'N' : 'Y';
        if (Object.keys(patch).length === 0) { onClose(); return; }
        await tablemaintService.update(TABLE, recno, patch);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save carrier profile.');
    } finally {
      setIsSaving(false);
    }
  }, [errors, isNew, form, confirmed, lastConfirm, formActive, wasActive, record, recno, onClose, onSaved, onCreated]);

  // DOT #/MC # input + its RMIS Lookup button.
  const lookupRow = (key: string, label: string, qtype: 'DOT' | 'MC_MX', required = false) => {
    const val = s(form[key]);
    const disabled = isSaving || !rmisOn;
    return (
      <div>
        <label className="block text-sm font-medium text-label mb-1">
          {label}{required && <span className="text-red-500"> *</span>}
        </label>
        <div className="flex gap-2">
          <input type="text" value={val} maxLength={15} disabled={disabled}
            onChange={(e) => setField(key, e.target.value)}
            className={`flex-1 ${inputCls(idInvalid, disabled)}`} />
          <button type="button" disabled={!val.trim() || isSaving}
            onClick={() => setRmisModal({ queryType: qtype, queryId: val.trim() })}
            className="shrink-0 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-40 inline-flex items-center gap-1">
            <MagnifyingGlassIcon className="w-4 h-4" /> RMIS Lookup
          </button>
        </div>
      </div>
    );
  };

  const txt = (key: string, label: string, opts: { required?: boolean; maxLength?: number; disabled?: boolean; invalid?: boolean } = {}) => (
    <TextField label={label} required={opts.required} invalid={opts.invalid} disabled={isSaving || opts.disabled}
      maxLength={opts.maxLength} value={s(form[key])} onChange={(v) => setField(key, v)} />
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Create Carrier Profile' : 'Save Changes'}</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add Carrier Profile' : `Edit Carrier Profile — ${s(record.truckername)} (#${recno})`;

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="60rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        {!isNew && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={recno} label={s(record.truckername)} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Carrier */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Carrier</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {txt('truckername', 'Carrier Name', { required: true, maxLength: 60, invalid: nameInvalid })}
            {/* Carrier flags. Tracking is independent of RMIS — it shares the box
                because both are carrier-level Y/N claims, not because one gates
                the other. */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <Toggle label="RMIS" checked={rmisOn} disabled={isSaving}
                onChange={(v) => setField('rmis', v ? 'Y' : 'N')}
                hint="When off, DOT #/MC #/SCAC are read-only (managed by RMIS)." />
              <Toggle label="Tracking" checked={trackingOn} disabled={isSaving}
                onChange={(v) => setField('tracking', v ? 'Y' : 'N')}
                hint="Does this carrier provide tracking." />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {lookupRow('dotnum', 'DOT #', 'DOT')}
            {lookupRow('mcnum', 'MC #', 'MC_MX')}
            {txt('truckerscac', 'SCAC', { maxLength: 10, disabled: !rmisOn })}
          </div>
          <div className="mt-4">{txt('truckeraddress', 'Address', { maxLength: 60 })}</div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {txt('truckercity', 'City', { maxLength: 25 })}
            {txt('truckerstate', 'State', { maxLength: 2 })}
            {txt('truckerzip', 'Zip', { maxLength: 10 })}
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <LockedField label="Confirmed"><BoolPill value={s(confirmed) === 'Y'} /></LockedField>
            <LockedField label="Last Confirmed">{fmtDateTime(lastConfirm) || <span className="text-gray-400">—</span>}</LockedField>
          </div>
        </fieldset>

        {/* Comments */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Comments</legend>
          <textarea value={s(form.comments)} disabled={isSaving} rows={5}
            onChange={(e) => setField('comments', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
        </fieldset>

        {/* Contacts — only once the carrier profile is saved. */}
        {!isNew ? (
          <CarrierContactsPanel carrierrecno={recno} />
        ) : (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-label">Contacts</legend>
            <p className="text-sm text-gray-400 dark:text-gray-500">Save the carrier profile first to add contacts.</p>
          </fieldset>
        )}

        {/* Active (soft-delete) — edit only. */}
        {!isNew && (
          <div className={`rounded-lg border p-3 ${formActive ? 'border-gray-200 dark:border-gray-700' : 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/20'}`}>
            <Toggle label="Active" checked={formActive} disabled={isSaving} onChange={setFormActive} />
            {!formActive && (
              <p className="mt-1 ml-7 text-xs font-medium text-red-700 dark:text-red-300">
                Inactive carriers are hidden from carrier lookups and dispatch. Re-check Active and save to restore.
              </p>
            )}
          </div>
        )}
      </div>

      {rmisModal && (
        <RmisLookupModal
          queryType={rmisModal.queryType}
          queryId={rmisModal.queryId}
          onClose={() => setRmisModal(null)}
          onApply={applyRmis}
        />
      )}
    </Drawer>
  );
};

export default CarrierProfileEditDrawer;
